'use client'

import React, { FC, ReactElement } from 'react'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { EXPLORER_URL, API_URL, NETWORK } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { useToast } from '@/components/ui/use-toast'
import useTokenOutpoints from '@/components/search-input/hooks/useTokenOutpoints'
import { ToastAction } from '@/components/ui/toast'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import { getInscriptionCollectionSlugs, inscriptionBelongsToCollection } from '@/utils/inscription-collections'

import useSelect from '../../hooks/useSelect'

import Invoice from '../../components/invoice'
import { Loading } from '@/components/ui/loading'
import SelectButton from '../../components/select-button'
import Item from './base-item'

import { getUnfreezeNet, UNFREEZE_POSTAGE_SATS as POSTAGE, UNFREEZE_ELIGIBILITY_SATS } from '@/utils/unfreeze'

const CollectionDetailRefund: FC<{
	tab: string
	onChangeTab: (v: string) => void
	onNavigate: (v: string) => void
	onClose: () => void
	args: any
	floorSats: string | number
	token: any
	classification: string
	collectionFilter?: string
}> = (props): ReactElement => {
	const { onClose, args, tab: _tab, onChangeTab: _onChangeTab, token, classification: _classification, collectionFilter } = props
	const { slug } = args
	const { toast } = useToast()
	const { exchangeRate } = useExchangeRate()
	const [computedRefundAmount, setComputedRefundAmount] = React.useState(0)
	const quoteRef = React.useRef<{ psbt: string; selection: string } | null>(null)
	const { data, isLoading } = useTokenOutpoints(token)
	const { data: allUtxos } = useSwr(
		`${API_URL}/wallet/${Wallet.balanceAddress()}/all-utxos`,
		fetcher
	)

	//
	// Remote Data
	//

	// Refund only shows inscriptions with sats > 546 (excess sats to reclaim)
	const refundableData = React.useMemo(() => {
		if (!data || !Array.isArray(data)) return []
		return data.filter((e) => e.sats > UNFREEZE_ELIGIBILITY_SATS)
	}, [data])

	const filteredData = React.useMemo(() => {
		if (!refundableData || !Array.isArray(refundableData)) return refundableData
		if (!collectionFilter || collectionFilter === 'all') return refundableData
		if (collectionFilter === 'uncategorized') {
			return refundableData.filter((e) => getInscriptionCollectionSlugs(e).length === 0)
		}
		return refundableData.filter((e) => inscriptionBelongsToCollection(e, collectionFilter))
	}, [refundableData, collectionFilter])

	const sortedData = React.useMemo(() => {
		if (!filteredData || !Array.isArray(filteredData)) return []
		return [...filteredData].sort((a, b) => (b.sats || 0) - (a.sats || 0))
	}, [filteredData])

	// Group inscriptions by their actual UTXO outpoint
	const utxoGroups = React.useMemo(() => {
		if (!data) return new Map()
		const groups = new Map<string, string[]>()
		for (const item of data) {
			const key = item.utxoOutpoint
			if (!key) continue
			if (!groups.has(key)) groups.set(key, [])
			groups.get(key)!.push(item.outpoint)
		}
		return groups
	}, [data])

	const ids = React.useMemo(() => {
		return sortedData.map((e) => e.outpoint)
	}, [sortedData])
	const { selected, setSelected, onSelect, onSelectAll, slider, onSliderChange, allSelected } = useSelect(ids)

	// Select/deselect all inscriptions on the same UTXO together
	const onSelectGroup = React.useCallback((id: string) => {
		const item = data?.find((e: any) => e.outpoint === id)
		if (!item?.utxoOutpoint) return onSelect(id)
		const group = utxoGroups.get(item.utxoOutpoint)
		if (!group || group.length <= 1) return onSelect(id)
		const isSelected = selected.has(id)
		const newSet = new Set(selected)
		for (const inscId of group) {
			if (isSelected) newSet.delete(inscId)
			else newSet.add(inscId)
		}
		setSelected(newSet)
	}, [data, utxoGroups, selected, onSelect, setSelected])

	// Get unique UTXO values for a selection set (dedup shared UTXOs)
	const getSelectedUtxoTotals = React.useCallback((sel: Set<string>) => {
		const utxoMap = new Map<string, number>()
		// @ts-ignore
		for (let e of sel.entries()) {
			const item = data?.find((a: any) => a.outpoint === e[0])
			if (item) utxoMap.set(item.utxoOutpoint || item.outpoint, item.sats || 0)
		}
		let totalValue = 0
		utxoMap.forEach((v) => { totalValue += v })
		return { totalValue, utxoCount: utxoMap.size }
	}, [data])

	//
	// Renders
	//

	const renderOutpoint = React.useCallback(
		(e: any) => {
			const id = e.outpoint
			const sats = e.sats || 0
			const usd = ((sats / 1e8) * exchangeRate)?.toFixed(2)
			const group = e.utxoOutpoint ? utxoGroups.get(e.utxoOutpoint) : null
			const groupSize = group?.length || 1
			return (
				<Item key={id} data={e} selected={selected.has(id)} onSelect={() => onSelectGroup(id)} slug={slug}>
					<p className="text-xs text-muted-foreground whitespace-nowrap">
						{formatNumber(sats)} sats / ${formatNumber(usd)}
						{groupSize > 1 && (
							<span className="ml-1 text-yellow-500">×{groupSize}</span>
						)}
					</p>
				</Item>
			)
		},
		[exchangeRate, utxoGroups, selected, slug, onSelectGroup]
	)

	const handleSubmit = React.useCallback(
		async (psbt: any) => {
			const { totalValue, utxoCount } = getSelectedUtxoTotals(selected)
			const net = getUnfreezeNet(totalValue - utxoCount * POSTAGE, Number(Wallet.getPSBTFee(psbt)), Wallet.getServiceFee(psbt))
			if (net === null || net <= 0) throw new Error('Fees exceed the excess BTC. Nothing to collect.')
			if (quoteRef.current?.psbt !== psbt || quoteRef.current?.selection !== [...selected].sort().join(',')) throw new Error('The selection changed. Wait for a new estimate.')
			const rawtx = await Wallet.signPSBTExtractTx(psbt)
			const { data: broadcastData } = await axios.post(`${API_URL}/wallet/broadcast`, { rawtx: rawtx })

			if (broadcastData.error) {
				toast({
					title: 'Failed to broadcast',
					description: broadcastData.error.message
				})
			} else {
				toast({
					title: 'Unfreeze successful',
					description: `Reclaimed ~${formatSatoshis(computedRefundAmount)} ${NETWORK}`,
					action: (
						<ToastAction
							altText="View Transaction"
							onClick={() => window.open(`${EXPLORER_URL}/tx/${broadcastData.result}`, '_blank')}
						>
							View Transaction
						</ToastAction>
					)
				})
			}

			onClose()
		},
		[onClose, toast, computedRefundAmount, getSelectedUtxoTotals, selected]
	)

	const handleBuildPsbt = React.useCallback(
		async (deps: any[], feeRate: number) => {
			const [dSelected] = deps

			if (dSelected.size === 0) {
				return null
			}

			if (!allUtxos?.utxos) {
				return null
			}

			const inscriptionIds = []

			for (let e of dSelected.entries()) {
				inscriptionIds.push(e[0])
			}

			const to = Wallet.getTaprootAddress()
			const changeAddress = Wallet.balanceAddress()

			const psbt = await Wallet.getTransfers(
				inscriptionIds,
				to,
				feeRate,
				allUtxos.utxos.map((e: any) => [e.txid, e.vout, e.value]),
				POSTAGE,
				changeAddress !== to ? changeAddress : undefined
			)

			const fee = Wallet.getPSBTFee(psbt)
			const serviceFee = Wallet.getServiceFee(psbt)
			const { totalValue, utxoCount } = getSelectedUtxoTotals(dSelected)
			const refundAmount = totalValue - utxoCount * POSTAGE - Number(fee) - serviceFee
			setComputedRefundAmount(refundAmount)
			quoteRef.current = { psbt, selection: [...dSelected].sort().join(',') }

			return { psbt, fee }
		},
		[allUtxos, getSelectedUtxoTotals]
	)

	const invoiceItems = React.useMemo(() => {
		return [
			{ title: 'Excess BTC before fees', valueFunc: () => {
				const { totalValue, utxoCount } = getSelectedUtxoTotals(selected)
				return `${formatNumber(totalValue - utxoCount * POSTAGE)} sats`
			}, emphasize: false },
			{
				title: 'You receive (after fees)',
				emphasize: true,
				valueFunc: ({ building, fee, psbt }: any) => {
					if (building || !psbt) return '--'
					const serviceFee = Wallet.getServiceFee(psbt)
					const { totalValue, utxoCount } = getSelectedUtxoTotals(selected)
					const refundAmount = totalValue - utxoCount * POSTAGE - Number(fee) - serviceFee
					return `${formatSatoshis(Math.max(0, refundAmount))} ${NETWORK} / $${formatNumber(
						((Math.max(0, refundAmount) / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			},
			{
				title: 'Total fees',
				valueFunc: ({ building, fee, psbt }: any) => {
					if (building || !psbt) return '--'
					const serviceFee = Wallet.getServiceFee(psbt)
					const totalFee = Number(fee) + serviceFee
					return `${formatSatoshis(totalFee)} ${NETWORK} / $${formatNumber(
						((totalFee / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			},
			{
				title: 'Service Fee',
				valueFunc: ({ building, psbt }: any) => {
					if (building || !psbt) return '--'
					const serviceFee = Wallet.getServiceFee(psbt)
					return `${formatSatoshis(serviceFee)} ${NETWORK} / $${formatNumber(
						((serviceFee / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			},
			{ title: 'Network Fee' }
		]
	}, [selected, exchangeRate, getSelectedUtxoTotals])

	// Clear selection when filter changes
	React.useEffect(() => {
		requestAnimationFrame(() => {
			setSelected(new Set())
		})
	}, [collectionFilter, setSelected])

	React.useEffect(() => {
		if (selected.size == 0 && sortedData.length) {
			// Auto-select the highest value inscription (and its UTXO group)
			onSelectGroup(sortedData[0].outpoint)
		}
	}, [selected, sortedData, onSelectGroup])

	return (
		<>
			<p className="text-xs text-muted-foreground px-2 pt-2 pb-1">
				Your inscription balance is not an available refund. This tool checks outputs above {UNFREEZE_ELIGIBILITY_SATS} sats and keeps {POSTAGE} sats per selected output with your inscriptions. You receive only the excess left after service and network fees.
			</p>
			{isLoading ? (
				<div className="flex items-center justify-center min-h-[120px]">
					<Loading size={16} />
				</div>
			) : sortedData?.length === 0 ? (
				<div className="p-4 text-center text-sm text-muted-foreground">No eligible excess BTC to reclaim. The BTC held with your inscriptions is not an available refund.</div>
			) : (
				<>
					<div className="flex justify-between items-center px-2 py-3 gap-2">
						<div className="flex gap-2 items-center">
							<SelectButton selected={allSelected} onSelect={onSelectAll} />
							<p className="text-xs text-muted-foreground select-none whitespace-nowrap">
								Quantity ({selected.size > 0 ? `${selected.size}/` : ''}{sortedData?.length} lots)
							</p>
						</div>
						<Slider
							defaultValue={[slider]}
							value={[slider]}
							max={sortedData?.length}
							step={1}
							onValueChange={onSliderChange}
						/>
					</div>
					{sortedData?.map(renderOutpoint)}
				</>
			)}
			<Invoice
				open={selected.size > 0}
				onBuild={handleBuildPsbt}
				buildDeps={[selected, allUtxos?.utxos]}
				onSubmit={handleSubmit}
				validateSubmit={({ building, fee, psbt }) => {
					if (building || !psbt) return undefined
					if (quoteRef.current?.psbt !== psbt || quoteRef.current?.selection !== [...selected].sort().join(',')) return 'Updating your estimate. Wait for the current selection.'
					const { totalValue, utxoCount } = getSelectedUtxoTotals(selected)
					const excess = totalValue - utxoCount * POSTAGE
					const fees = Number(fee) + Wallet.getServiceFee(psbt)
					const net = getUnfreezeNet(excess, Number(fee), Wallet.getServiceFee(psbt))
					if (net === null) return 'Unable to verify the amount. Nothing will be collected.'
					if (net <= 0) return `Nothing to collect at these fees. ${formatNumber(excess)} excess sats cannot cover ${formatNumber(fees)} sats in fees. Your inscriptions stay unchanged.`
					return undefined
				}}
				action={({ building, fee, psbt }) => {
					if (building || !psbt) return 'Calculating...'
					const serviceFee = Wallet.getServiceFee(psbt)
					const { totalValue, utxoCount } = getSelectedUtxoTotals(selected)
					const refundAmount = totalValue - utxoCount * POSTAGE - Number(fee) - serviceFee
					if (refundAmount <= 0) return 'Nothing to collect'
					const usd = ((Math.max(0, refundAmount) / 1e8) * exchangeRate)?.toFixed(2)
					return `Collect ${formatNumber(refundAmount)} sats${exchangeRate > 0 ? ` ($${formatNumber(usd)})` : ''}`
				}}
				items={invoiceItems}
			/>
		</>
	)
}

export default CollectionDetailRefund
